import React, { useEffect, useState } from "react";
import { usePopper } from "react-popper";
import { styled } from "styled-components";
import { Box, Flex } from "../../../../components/Box";
import HeaderIconWalletInfo from "../../../../components/Svg/Icons/HeaderIconWalletInfo";
import MenuIcon, { MenuIconWrapper } from "./MenuIcon";
import { UserMenuItem } from "./styles";
import { UserMenuProps, variants } from "./types";

export const StyledUserMenu = styled(Flex)`
  display: flex;
  // width: 228.5px;
  height: 56px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  border-radius: 600px;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0px 0px 32px 0px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(24px);
  cursor: pointer;

  &:hover {
    opacity: 0.65;
  }
`;

export const LabelText = styled.div`
  color: #fff;
  font-family: "Space Mono";
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 132%; /* 23.76px */
  letter-spacing: -1.44px;
`;

const Menu = styled.div<{ $isOpen: boolean }>`
  // background-color: ${({ theme }) => theme.card.background};
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  border-radius: 16px;
  padding-bottom: 4px;
  padding-top: 4px;
  pointer-events: auto;
  width: 280px;
  visibility: visible;
  z-index: 1001;

  ${({ $isOpen }) =>
    !$isOpen &&
    `
    pointer-events: none;
    visibility: hidden;
  `}

  ${UserMenuItem}:first-child {
    border-radius: 8px 8px 0 0;
  }

  ${UserMenuItem}:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const WalletInfo = styled.div`
  display: flex;
  padding-left: 8px;
  align-items: center;
  gap: 12px;
`;

const RoundedRectangle = styled.div`
  width: 0.5px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 30px;
  background: rgba(217, 217, 217, 0.5);
`;

const MCButton = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 54.054px;
  background: var(--Brand-Primary, #f59c53);
  box-shadow: 0px 1.081px 8.649px 0px rgba(0, 0, 0, 0.1), -2.162px -2.162px 3.243px 0px rgba(130, 64, 9, 0.5) inset,
    2.162px 2.162px 3.243px 0px rgba(250, 192, 145, 0.5) inset;
`;

const UserMenu: React.FC<UserMenuProps> = ({
  account,
  text,
  avatarSrc,
  avatarClassName,
  variant = variants.DEFAULT,
  children,
  disabled,
  placement = "bottom-end",
  recalculatePopover,
  ellipsis = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [targetRef, setTargetRef] = useState<HTMLDivElement | null>(null);
  const [tooltipRef, setTooltipRef] = useState<HTMLDivElement | null>(null);

  const { styles, attributes, update } = usePopper(targetRef, tooltipRef, {
    strategy: "fixed",
    placement,
    modifiers: [{ name: "offset", options: { offset: [0, 0] } }],
  });

  const accountEllipsis = account ? `${account.substring(0, 2)}...${account.substring(account.length - 4)}` : null;

  // recalculate the popover position
  useEffect(() => {
    if (recalculatePopover && isOpen && update) update();
  }, [isOpen, update, recalculatePopover]);

  useEffect(() => {
    const showDropdownMenu = () => {
      setIsOpen(true);
    };

    const hideDropdownMenu = (evt: MouseEvent | TouchEvent) => {
      const target = evt.target as Node;
      if (target && !tooltipRef?.contains(target)) {
        setIsOpen(false);
        evt.stopPropagation();
      }
    };

    targetRef?.addEventListener("mouseenter", showDropdownMenu);
    targetRef?.addEventListener("mouseleave", hideDropdownMenu);

    return () => {
      targetRef?.removeEventListener("mouseenter", showDropdownMenu);
      targetRef?.removeEventListener("mouseleave", hideDropdownMenu);
    };
  }, [targetRef, tooltipRef, setIsOpen]);

  return (
    <Flex alignItems="center" height="100%" ref={setTargetRef} {...props}>
      <StyledUserMenu
        onTouchStart={() => {
          setIsOpen((s) => !s);
        }}
      >
        <WalletInfo>
          <MenuIconWrapper bordercolor="primary">
            <HeaderIconWalletInfo width={32} height={32} />
          </MenuIconWrapper>
          <LabelText title={typeof text === "string" ? text || account : account}>
            {text || (ellipsis ? accountEllipsis : account)}
          </LabelText>
          {/* <LabelText>-</LabelText> */}
        </WalletInfo>
        <RoundedRectangle />
        <MCButton>
          <MenuIcon className={avatarClassName} avatarSrc={avatarSrc} variant={variant} />
        </MCButton>
        {/* {!disabled && <ChevronDownIcon color="text" width="24px" />} */}
      </StyledUserMenu>
      {!disabled && (
        <Menu style={styles.popper} ref={setTooltipRef} {...attributes.popper} $isOpen={isOpen}>
          <Box onClick={() => setIsOpen(false)}>{children?.({ isOpen })}</Box>
        </Menu>
      )}
    </Flex>
  );
};

export default UserMenu;
